import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  CircleHelp, Package, Plus, Trash2, Undo2, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  createQuizOption, createQuizProduct, createQuizQuestion, loadProducts, loadQuiz, loadQuizResponses, loadQuizSummary,
  loadTranslationCatalogue, setQuizLifts, setQuizOptionOrder, setQuizProductOrder, setQuizQuestionOrder, updateQuizOption,
  updateQuizProduct, updateQuizQuestion,
} from './api';
import { PhotoField, WordField, friendly } from './HubFields';
import { Arrows, Line } from './HubPageEditor';
import { tx } from './i18n';
import {
  Alert, Bars, Failed, Head, Loading, Metric, Metrics, Pill, SearchField, WorkspaceTabs, XomicsSelect,
} from './parts';
import { Switch } from './ProductOptions';
import {
  ENGLISH, SITE_TEXT, activeLanguages, languageLabel, ownWords, saveTexts, unix, wordsOf,
} from './siteWords';
import SiteLanguage from './SiteLanguage';
import { ForStoreOffice, StoreOfficeLine } from './StoreOffice';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import './productDetail.css';
import './products.css';
import './help-centre.css';
import './hub.css';
import './quiz.css';

// The store's quiz ("Take the quiz"), written here.
//
// The quiz asks its questions one after another. Every answer points to one
// or more of our products, each with a strength; at the end the product that
// was pointed to the most is recommended first and the next two go with it.
// XOmics keeps the questions, their answers, the products the quiz can
// recommend and what each answer recommends (/Quiz), and counts what visitors
// answered and where they were; the words are storefront site text, written
// through the translation endpoints one language at a time, like the Hub's.
//
// A question, an answer or a product is added, moved, switched off or removed
// at once; words are typed and then saved together. What the quiz asks and
// recommends is the same in every language. A product that is not on sale is
// kept here and left out by the store until it is.

const STRENGTHS = [1, 2, 3, 4];
const PERIODS = [7, 30, 90, 3650];
const QUESTION_FIELDS = [['question', 'line'], ['why', 'line']];
const PAGE = 50;

const countryName = (code) => {
  if (!code) return tx('ui.quiz.answers.unknown');
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code; } catch { return code; }
};
const when = (ms) => new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const share = (count, total) => (total > 0 ? `${Math.round((count / total) * 100)}%` : '0%');

// ------------------------------------------------------------------ questions

// Any of our products, chosen from a list in a form of its own, for an answer
// to recommend. A row is a product, found by its name; choosing one closes the
// form. One that is not on sale says so: the store leaves it out until it is.
function ProductPick({ title, products, onPick, onClose }) {
  const [query, setQuery] = useState('');
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  const needle = query.trim().toLowerCase();
  const found = products.filter((product) => needle === '' || product.name.toLowerCase().includes(needle));
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="xo-pc-form is-narrow" role="dialog" aria-modal="true" aria-labelledby="xo-quiz-pick-title"
      onMouseDown={(event) => event.stopPropagation()}>
      <header className="xo-pc-form-head">
        <span className="xo-pc-thumb is-icon"><Package aria-hidden="true" /></span>
        <div><h2 id="xo-quiz-pick-title">{title}</h2></div>
        <button type="button" className="xo-icon-btn" aria-label={tx('ui.common.cancel')} onClick={onClose}><X aria-hidden="true" /></button>
      </header>
      <div className="xo-pc-form-body">
        <SearchField value={query} onChange={setQuery} placeholder={tx('ui.quiz.products.search')} label={tx('ui.quiz.products.search')} />
        <ul className="xo-quiz-pick">
          {found.map((product) => <li key={product.id}>
            <button type="button" onClick={() => onPick(product)}>
              <strong>{product.name}</strong>
              {product.isActive === false && <Pill tone="warn">{tx('ui.quiz.products.not_on_sale')}</Pill>}
            </button></li>)}
        </ul>
      </div>
    </section>
  </div>;
}

// The quiz's own row of one of our products, made the first time the product
// is recommended: nobody lists a product for the quiz before it can be chosen.
async function quizRowOf(quiz, ours) {
  const listed = quiz.products.find((product) => product.productId === ours.id);
  return listed ? listed.id : (await createQuizProduct(ours.id)).id;
}

// What an answer recommends: our products it points to, each with how
// strongly. A person picks any of our products by its name; nobody types a code.
function Recommends({ option, quiz, ours, write, act }) {
  const [adding, setAdding] = useState(false);
  const products = quiz.products;
  const byId = new Map(products.map((product) => [product.id, product]));
  const recommended = new Set(option.lifts.map((lift) => byId.get(lift.productId)?.productId));
  const free = ours.filter((product) => !recommended.has(product.id));
  const save = (lifts) => act(() => setQuizLifts(option.id, lifts));
  return <div className="xo-quiz-lifts">
    <span className="xo-hub-label">{tx('ui.quiz.recommends.title')}</span>
    {option.lifts.length === 0 && <p className="xo-hub-field-note">{tx('ui.quiz.recommends.none')}</p>}
    {option.lifts.map((lift) => {
      const product = byId.get(lift.productId);
      return <div key={lift.productId} className="xo-quiz-lift">
        <strong>{product?.name ?? `#${lift.productId}`}</strong>
        {product && !product.onSale && <Pill tone="warn">{tx('ui.quiz.products.not_on_sale')}</Pill>}
        <div className="xo-quiz-strength" role="radiogroup" aria-label={tx('ui.quiz.strength.title', { name: product?.name ?? '' })}>
          {STRENGTHS.map((weight) => <button key={weight} type="button" role="radio" aria-checked={lift.weight === weight}
            aria-label={tx(`ui.quiz.strength.${weight}`)} title={tx(`ui.quiz.strength.${weight}`)} disabled={!write}
            className={lift.weight >= weight ? 'is-on' : ''}
            onClick={() => save(option.lifts.map((entry) => (entry.productId === lift.productId ? { ...entry, weight } : entry)))} />)}
          <span>{tx(`ui.quiz.strength.${lift.weight}`)}</span>
        </div>
        <button type="button" className="xo-icon-btn" disabled={!write} aria-label={tx('ui.quiz.recommends.remove', { name: product?.name ?? '' })}
          onClick={() => save(option.lifts.filter((entry) => entry.productId !== lift.productId))}><X aria-hidden="true" /></button>
      </div>;
    })}
    {free.length > 0 && <div className="xo-quiz-lift-add">
      <button type="button" className="xo-btn-2" disabled={!write} onClick={() => setAdding(true)}>
        <Plus aria-hidden="true" />{tx('ui.quiz.recommends.add')}</button>
    </div>}
    {adding && <ProductPick title={tx('ui.quiz.recommends.add')} products={free} onClose={() => setAdding(false)}
      onPick={(product) => {
        setAdding(false);
        act(async () => setQuizLifts(option.id, [...option.lifts, { productId: await quizRowOf(quiz, product), weight: 2 }]));
      }} />}
  </div>;
}

// One product the quiz recommends, in a form of its own: what the result says
// about it, where it stands among the others (ties between products go to the
// one that stands first), whether the quiz shows it, and the way out of the
// quiz. Its words are saved with the form's one Save.
function ProductForm({
  product, index, count, editor, onMove, onSave, onClose,
}) {
  const {
    write, act, language, valueOf, type, onRemoved,
  } = editor;
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onClose}>
    <form className="xo-pc-form" role="dialog" aria-modal="true" aria-labelledby="xo-quiz-product-title"
      onSubmit={async (event) => { event.preventDefault(); if (await onSave()) onClose(); }} onMouseDown={(event) => event.stopPropagation()}>
      <header className="xo-pc-form-head">
        <span className="xo-pc-thumb is-icon"><Package aria-hidden="true" /></span>
        <div>
          <h2 id="xo-quiz-product-title">{product.name}</h2>
          {!product.onSale && <p><Pill tone="warn">{tx('ui.quiz.products.not_on_sale')}</Pill></p>}
        </div>
        <button type="button" className="xo-icon-btn" aria-label={tx('ui.products.close_bbfa773e')} onClick={onClose}><X aria-hidden="true" /></button>
      </header>
      <div className="xo-pc-form-body xo-quiz-product">
        <WordField kind="text" label={tx('ui.quiz.field.product.blurb')} text={product.words.blurb} language={language}
          value={valueOf(product.words.blurb)} disabled={!write} hub={{ front: null, pages: [] }} products={[]}
          onChange={(value) => type(product.words.blurb, value)} />
        <div className="xo-quiz-product-line">
          <span>{tx('ui.quiz.shown')}</span>
          <Arrows canUp={index > 0} canDown={index < count - 1} disabled={!write} onUp={() => onMove(-1)} onDown={() => onMove(1)} />
          <Switch on={product.isActive} disabled={!write} label={tx('ui.quiz.shown')}
            onChange={(isActive) => act(() => updateQuizProduct(product.id, { isActive }))} />
        </div>
        <div className="xo-hub-card-foot">
          <button type="button" className="xo-link is-danger" disabled={!write}
            onClick={() => act(() => updateQuizProduct(product.id, { isRemoved: true }), () => { onRemoved({ product: product.id }); onClose(); })}>
            <Trash2 aria-hidden="true" />{tx('ui.quiz.products.remove')}</button>
        </div>
      </div>
      <footer className="xo-pc-form-foot">
        <span />
        <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.products.close_bbfa773e')}</button>
        <button type="submit" className="xo-btn" disabled={!write}>{tx('ui.hub.save.save')}</button>
      </footer>
    </form>
  </div>;
}

// What the quiz recommends, all of it at once: every answer down the side,
// every product the quiz can recommend across, and how strongly the one points
// to the other where they meet. Pressing a meeting point makes it one
// stronger, and after the strongest, nothing: nobody opens an answer to see or
// change what it leads to.
// A product is brought in by a button: any of ours that is not in the table
// yet. A product's name across the top opens its own form.
// Our products the quiz does not recommend yet: what can still be added to it.
const freeOf = (quiz, ours) => {
  const listed = new Set(quiz.products.map((product) => product.productId));
  return ours.filter((product) => !listed.has(product.id));
};

function RecommendsTab({
  quiz, editor, adding, onAdding, onSave,
}) {
  const {
    write, act, language, valueOf, ours,
  } = editor;
  const [openId, setOpenId] = useState(null);
  const open = quiz.products.find((product) => product.id === openId) ?? null;
  const free = freeOf(quiz, ours);
  const move = (index, by) => {
    const next = quiz.products.map((product) => product.id);
    [next[index], next[index + by]] = [next[index + by], next[index]];
    act(() => setQuizProductOrder(next));
  };
  const said = (text) => friendly(valueOf(text) || wordsOf(text, language));
  const strengthOf = (option, product) => option.lifts.find((lift) => lift.productId === product.id)?.weight ?? 0;
  const press = (option, product) => {
    const next = (strengthOf(option, product) + 1) % (STRENGTHS.length + 1);
    const others = option.lifts.filter((lift) => lift.productId !== product.id);
    act(() => setQuizLifts(option.id, next === 0 ? others : [...others, { productId: product.id, weight: next }]));
  };
  return <div className="xo-quiz-recommends">
    {/* What the numbers are. */}
    <p className="xo-quiz-key">{STRENGTHS.map((weight) => <span key={weight}>
      <i className={`xo-quiz-cell is-${weight}`} aria-hidden="true">{weight}</i>{tx(`ui.quiz.strength.${weight}`)}</span>)}</p>
    <div className="xo-quiz-matrix" role="table" aria-label={tx('ui.quiz.recommends.table')}
      style={{ '--xo-quiz-columns': quiz.products.length }}>
      <div role="row" className="is-head">
        <span role="columnheader">{tx('ui.quiz.answers_of')}</span>
        {quiz.products.map((product) => <span key={product.id} role="columnheader">
          <button type="button" className={`xo-quiz-product-name${product.onSale && product.isActive ? '' : ' is-off'}`}
            title={product.name} onClick={() => setOpenId(product.id)}>{product.name}</button></span>)}
      </div>
      {quiz.questions.map((question, index) => <React.Fragment key={question.id}>
        <div role="row" className="is-question">
          <span role="cell">{index + 1}. {said(question.words.question) || tx('ui.hub.section.empty')}</span>
        </div>
        {question.options.map((option) => {
          const label = said(option.words.label) || tx('ui.hub.section.empty');
          return <div key={option.id} role="row" className={option.isActive && question.isActive ? '' : 'is-off'}>
            <span role="rowheader" dir="auto">{label}</span>
            {quiz.products.map((product) => {
              const weight = strengthOf(option, product);
              return <span key={product.id} role="cell">
                <button type="button" className={`xo-quiz-cell is-${weight}`} disabled={!write}
                  aria-label={`${label}, ${product.name}: ${tx(`ui.quiz.strength.${weight}`)}`} title={tx(`ui.quiz.strength.${weight}`)}
                  onClick={() => press(option, product)}>{weight > 0 ? weight : ''}</button>
              </span>;
            })}
          </div>;
        })}
      </React.Fragment>)}
    </div>
    {adding && <ProductPick title={tx('ui.quiz.products.add')} products={free} onClose={() => onAdding(false)}
      onPick={(product) => { onAdding(false); act(() => createQuizProduct(product.id)); }} />}
    {open && <ProductForm product={open} index={quiz.products.indexOf(open)} count={quiz.products.length} editor={editor}
      onMove={(by) => move(quiz.products.indexOf(open), by)} onSave={onSave} onClose={() => setOpenId(null)} />}
  </div>;
}

function AnswerRow({
  option, first, number, editor, canUp, canDown, onUp, onDown,
}) {
  const {
    write, act, language, valueOf, type, quiz, ours, products, onRemoved,
  } = editor;
  const [open, setOpen] = useState(false);
  const set = (changes) => act(() => updateQuizOption(option.id, changes));
  const field = (name, kind) => <WordField key={name} kind={kind} field={name} label={tx(`ui.quiz.field.option.${name}`)}
    text={option.words[name]} language={language} value={valueOf(option.words[name])} disabled={!write}
    hub={{ front: null, pages: [] }} products={[]} onChange={(value) => type(option.words[name], value)} />;
  const names = option.lifts.map((lift) => products.find((product) => product.id === lift.productId)?.name).filter(Boolean);
  return <div className={`xo-hub-item${open ? ' is-open' : ''}${option.isActive ? '' : ' is-off'}`}>
    <Line className="xo-hub-item-head" open={open} onToggle={() => setOpen(!open)}
      arrows={<Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      picture={option.photo ? <img className="xo-hub-item-thumb" src={option.photo.thumbnailUrl || option.photo.url} alt="" />
        : <span className="xo-hub-item-thumb is-icon xo-quiz-number">{number}</span>}
      name={friendly(valueOf(option.words.label) || wordsOf(option.words.label, language)) || tx('ui.hub.section.empty')}
      words={names.length > 0 ? tx('ui.quiz.recommends.says', { products: names.join(', ') }) : tx('ui.quiz.recommends.none')}
      shown={<Switch on={option.isActive} disabled={!write} label={tx('ui.quiz.shown')} onChange={(isActive) => set({ isActive })} />} />
    {open && <div className="xo-hub-item-body">
      {field('label', 'line')}
      <Recommends option={option} quiz={quiz} ours={ours} write={write} act={act} />
      {first && <div className="xo-quiz-card-extra">
        <PhotoField photo={option.photo} canFocus disabled={!write} onSet={set} />
        {field('line', 'line')}
        {field('alt', 'line')}
      </div>}
      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateQuizOption(option.id, { isRemoved: true }), () => onRemoved({ option: option.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.quiz.answer.remove')}</button>
      </div>
    </div>}
  </div>;
}

function QuestionCard({
  question, index, count, open, onToggle, editor,
}) {
  const {
    write, act, language, valueOf, type, onRemoved, questionIds,
  } = editor;
  const move = (by) => {
    const next = [...questionIds];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    act(() => setQuizQuestionOrder(next));
  };
  const optionIds = question.options.map((option) => option.id);
  const moveOption = (at, by) => {
    const next = [...optionIds];
    [next[at], next[at + by]] = [next[at + by], next[at]];
    act(() => setQuizOptionOrder(question.id, next));
  };
  // What a question is called in the list: its words in the language written, or the English until it has them.
  const title = friendly(valueOf(question.words.question) || wordsOf(question.words.question, language));
  return <section className={`xo-hub-card${open ? ' is-open' : ''}${question.isActive ? '' : ' is-off'}`}
    aria-label={`${tx('ui.quiz.question.number', { number: index + 1 })}${title ? `: ${title}` : ''}`}>
    <Line className="xo-hub-card-head" open={open} onToggle={onToggle}
      arrows={<Arrows canUp={index > 0} canDown={index < count - 1} disabled={!write} onUp={() => move(-1)} onDown={() => move(1)} />}
      picture={<span className="xo-hub-card-icon"><CircleHelp aria-hidden="true" /></span>}
      name={tx('ui.quiz.question.number', { number: index + 1 })} words={title || tx('ui.hub.section.empty')}
      pills={!question.isActive && <Pill tone="flat">{tx('ui.hub.section.off')}</Pill>}
      shown={<Switch on={question.isActive} disabled={!write} label={tx('ui.quiz.shown')}
        onChange={(isActive) => act(() => updateQuizQuestion(question.id, { isActive }))} />} />
    {open && <div className="xo-hub-card-body">
      {QUESTION_FIELDS.map(([name, kind]) => <WordField key={name} kind={kind} field={name} label={tx(`ui.quiz.field.question.${name}`)}
        text={question.words[name]} language={language} value={valueOf(question.words[name])} disabled={!write}
        hub={{ front: null, pages: [] }} products={[]} onChange={(value) => type(question.words[name], value)} />)}
      <div className="xo-hub-items">
        <span className="xo-hub-label">{tx('ui.quiz.answers_of')}</span>
        {question.options.map((option, at) => <AnswerRow key={option.id} option={option} first={index === 0} number={at + 1} editor={editor}
          canUp={at > 0} canDown={at < question.options.length - 1} onUp={() => moveOption(at, -1)} onDown={() => moveOption(at, 1)} />)}
        <div className="xo-hub-items-add">
          <button type="button" className="xo-btn-2" disabled={!write} onClick={() => act(() => createQuizOption(question.id))}>
            <Plus aria-hidden="true" />{tx('ui.quiz.answer.add')}</button>
        </div>
      </div>
      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateQuizQuestion(question.id, { isRemoved: true }), () => onRemoved({ question: question.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.quiz.question.remove')}</button>
      </div>
    </div>}
  </section>;
}

// -------------------------------------------------------------------- answers

function AnswersTab({ quiz, languages, days }) {
  const languageName = (code) => languages.find((entry) => entry.code === code)?.name ?? code;
  const summary = useLiveResource([], useCallback(() => loadQuizSummary(days), [days]));
  const [latest, setLatest] = useState({ responses: [], nextBeforeId: null, loaded: false, failed: '' });
  const more = useCallback(async (beforeId) => {
    try {
      const page = await loadQuizResponses({ limit: PAGE, beforeId });
      setLatest((current) => ({
        responses: beforeId === null ? page.responses : [...current.responses, ...page.responses],
        nextBeforeId: page.nextBeforeId, loaded: true, failed: '',
      }));
    } catch (error) {
      setLatest((current) => ({ ...current, loaded: true, failed: errorMessage(error) }));
    }
  }, []);
  useEffect(() => { more(null); }, [more]);

  const productName = (id) => quiz.products.find((product) => product.id === id)?.name ?? tx('ui.quiz.answers.unknown');
  const options = new Map(quiz.questions.flatMap((question) => question.options.map((option) => [option.id, friendly(wordsOf(option.words.label, ENGLISH))])));
  if (summary.error && !summary.data) return <Failed message={summary.error} onRetry={summary.refresh} />;
  if (!summary.data) return <Loading label={tx('ui.quiz.loading')} />;
  const data = summary.data;
  const bars = (rows, name) => (rows.length === 0 ? <p className="xo-hub-field-note">{tx('ui.quiz.answers.none')}</p>
    : <Bars rows={rows.map((row) => ({ label: name(row), value: row.count, display: `${row.count} · ${share(row.count, data.inSpan)}` }))} />);
  return <div className="xo-quiz-answers">
    <Metrics>
      <Metric label={tx('ui.quiz.answers.finished')} value={data.inSpan} note={tx(`ui.quiz.answers.period_${days}`)} />
      <Metric label={tx('ui.quiz.answers.last_week')} value={data.lastWeek} />
      <Metric label={tx('ui.quiz.answers.all_time')} value={data.total} />
    </Metrics>
    {data.total === 0 && <Alert tone="info">{tx('ui.quiz.answers.empty')}</Alert>}
    <div className="xo-quiz-grid">
      <section className="xo-hub-settings"><strong>{tx('ui.quiz.answers.leads')}</strong>
        {bars(data.leads.filter((row) => row.productId !== null), (row) => productName(row.productId))}</section>
      <section className="xo-hub-settings"><strong>{tx('ui.quiz.answers.countries')}</strong>
        {bars(data.countries, (row) => countryName(row.name))}</section>
      <section className="xo-hub-settings"><strong>{tx('ui.quiz.answers.devices')}</strong>
        {bars(data.devices, (row) => (row.name ? tx(`ui.quiz.device.${row.name}`) : tx('ui.quiz.answers.unknown')))}</section>
      <section className="xo-hub-settings"><strong>{tx('ui.quiz.answers.languages')}</strong>
        {bars(data.languages, (row) => languageName(row.name))}</section>
    </div>
    <section className="xo-hub-settings">
      <strong>{tx('ui.quiz.answers.by_question')}</strong>
      {quiz.questions.map((question, index) => {
        const counts = question.options.map((option) => ({
          option, count: data.answers.find((row) => row.optionId === option.id)?.count ?? 0,
        }));
        const asked = counts.reduce((sum, row) => sum + row.count, 0);
        return <div key={question.id} className="xo-quiz-question-answers">
          <p>{index + 1}. {friendly(wordsOf(question.words.question, ENGLISH))}</p>
          <Bars rows={counts.map((row) => ({
            label: friendly(wordsOf(row.option.words.label, ENGLISH)) || `#${row.option.id}`, value: row.count,
            display: `${row.count} · ${share(row.count, asked)}`,
          }))} />
        </div>;
      })}
    </section>
    <section className="xo-hub-settings">
      <strong>{tx('ui.quiz.answers.latest')}</strong>
      {latest.failed && <Alert tone="bad">{latest.failed}</Alert>}
      {latest.loaded && latest.responses.length === 0 && <p className="xo-hub-field-note">{tx('ui.quiz.answers.none')}</p>}
      <ul className="xo-quiz-latest">
        {latest.responses.map((response) => <li key={response.id}>
          <details>
            <summary>
              <span>{when(response.at)}</span>
              <span>{[response.city, countryName(response.country)].filter(Boolean).join(', ')}</span>
              <span>{response.device ? tx(`ui.quiz.device.${response.device}`) : ''}</span>
              <span>{languageName(response.language)}</span>
              <strong>{response.products.length > 0 ? productName(response.products[0]) : ''}</strong>
            </summary>
            <ol>{response.answers.map((answer) => <li key={answer.questionId}>{options.get(answer.optionId) ?? `#${answer.optionId}`}</li>)}</ol>
            {response.products.length > 1 && <p className="xo-hub-field-note">
              {tx('ui.quiz.answers.also', { products: response.products.slice(1).map(productName).join(', ') })}</p>}
          </details>
        </li>)}
      </ul>
      {latest.nextBeforeId !== null && <button type="button" className="xo-btn-2" onClick={() => more(latest.nextBeforeId)}>
        {tx('ui.quiz.answers.more')}</button>}
    </section>
  </div>;
}

// ----------------------------------------------------------------------- page

// The quiz is one, shared by the offices; which of its questions, answers and
// products an office's store shows is that office's own, so the page is one office's.
export default function XomicsQuiz() {
  return <ForStoreOffice title={tx('ui.quiz.title')} className="xo-platform-page xo-help-page xo-hub xo-quiz">
    {(store) => <QuizPage store={store} />}
  </ForStoreOffice>;
}

function QuizPage({ store }) {
  const { permissions } = useXomicsAuth();
  const canManage = permissions.includes('CONTENT_MANAGE');
  const [language, setLanguage] = useState(ENGLISH);
  const [tab, setTab] = useState('questions');
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [undo, setUndo] = useState(null);
  // The list a product is added from, opened by the button in the head.
  const [adding, setAdding] = useState(false);
  // How far back what people answered is counted, chosen where the language is on the other tabs.
  const [days, setDays] = useState(30);
  const catalogue = useLiveResource(['translations'], useCallback(() => loadTranslationCatalogue(), []));
  const one = useLiveResource(['translations'], useCallback(() => loadQuiz(language), [language]));
  const sold = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  const quiz = one.data;
  const languages = useMemo(() => (catalogue.data ? activeLanguages(catalogue.data) : []), [catalogue.data]);
  const siteText = catalogue.data?.contentTypes.find((entry) => entry.code === SITE_TEXT) ?? null;
  useEffect(() => { setDrafts({}); setNotice(null); setUndo(null); }, [language]);

  const texts = useMemo(() => {
    const byKey = new Map();
    if (!quiz) return byKey;
    const take = (words) => Object.values(words).forEach((text) => byKey.set(text.key, text));
    quiz.questions.forEach((question) => { take(question.words); question.options.forEach((option) => take(option.words)); });
    quiz.products.forEach((product) => take(product.words));
    take(quiz.words);
    return byKey;
  }, [quiz]);
  const stored = useCallback((text) => friendly(ownWords(text, language)), [language]);
  const valueOf = useCallback((text) => (drafts[text.key] !== undefined ? drafts[text.key] : stored(text)), [drafts, stored]);
  const type = useCallback((text, value) => setDrafts((current) => ({ ...current, [text.key]: value })), []);
  const dirty = useMemo(() => Object.keys(drafts).filter((key) => texts.has(key) && unix(drafts[key]) !== stored(texts.get(key))),
    [drafts, stored, texts]);
  useEffect(() => {
    if (dirty.length === 0) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty.length]);

  const refresh = one.refresh;
  const act = useCallback(async (work, done) => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await work();
      await refresh();
      if (done) done(result);
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  if ((one.error && !quiz) || (catalogue.error && !catalogue.data)) {
    return <Failed message={one.error || catalogue.error} onRetry={() => { one.refresh(); catalogue.refresh(); }} />;
  }
  if (!quiz || !catalogue.data || !siteText) return <Loading label={tx('ui.quiz.loading')} />;

  const write = canManage && !busy;
  const other = language !== ENGLISH;
  // Saves the words that were typed, all of them together; says whether it did.
  async function save() {
    if (dirty.length === 0) return true;
    setBusy(true);
    setNotice(null);
    try {
      await saveTexts(dirty.map((key) => [texts.get(key), unix(drafts[key])]), { language, contentTypeId: siteText.id, source: 'quiz' });
      await one.refresh();
      setDrafts({});
      setNotice({ tone: 'ok', text: tx('ui.hub.save.saved') });
      return true;
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
      return false;
    } finally {
      setBusy(false);
    }
  }
  // Any product of the catalogue can be recommended; the store leaves out one that is not on sale where the visitor is.
  const ours = sold.data || [];
  const editor = {
    write, act, language, valueOf, type, quiz, ours, products: quiz.products, onRemoved: setUndo,
    questionIds: quiz.questions.map((question) => question.id),
  };
  const putBack = () => act(() => {
    if (undo.option) return updateQuizOption(undo.option, { isRemoved: false });
    if (undo.product) return updateQuizProduct(undo.product, { isRemoved: false });
    return updateQuizQuestion(undo.question, { isRemoved: false });
  }, () => setUndo(null));
  const tabs = [
    { value: 'questions', label: tx('ui.quiz.tab.questions'), count: quiz.questions.length },
    { value: 'recommends', label: tx('ui.quiz.tab.recommends') },
    { value: 'answers', label: tx('ui.quiz.tab.answers') },
  ];

  return <div className="xo-platform-page xo-help-page xo-hub xo-quiz">
    {/* What the open tab adds is the button in the head: a question, or a product to recommend. */}
    <Head title={tx('ui.quiz.title')} under={<StoreOfficeLine store={store} />}>
      {tab === 'questions' && canManage && <button type="button" className="xo-btn" disabled={!write}
        onClick={() => act(() => createQuizQuestion(), (created) => setOpenId(created.id))}>
        <Plus aria-hidden="true" />{tx('ui.quiz.question.add')}</button>}
      {tab === 'recommends' && canManage && freeOf(quiz, ours).length > 0 && <button type="button" className="xo-btn" disabled={!write}
        onClick={() => setAdding(true)}><Plus aria-hidden="true" />{tx('ui.quiz.products.add')}</button>}
    </Head>
    {!canManage && <Alert tone="info">{tx('ui.quiz.read_only')}</Alert>}
    <div className="xo-product-navigation">
      <div className="xo-product-section-row">
        <WorkspaceTabs className="xo-product-sections" value={tab} tabs={tabs} label={tx('ui.quiz.title')}
          onChange={(next) => { if (dirty.length === 0 || window.confirm(tx('ui.hub.save.leave'))) { setDrafts({}); setTab(next); } }} />
        <div className="xo-product-toolbar-actions">
          {tab !== 'answers' && <SiteLanguage label={tx('ui.hub.page.language')} value={language} languages={languages}
            onChange={(code) => { if (dirty.length === 0 || window.confirm(tx('ui.hub.save.leave'))) setLanguage(code); }} />}
          {tab === 'answers' && <XomicsSelect className="xo-toolbar-choice" value={days} aria-label={tx('ui.quiz.answers.period')}
            onChange={(event) => setDays(Number(event.target.value))}>
            {PERIODS.map((period) => <option key={period} value={period}>{tx(`ui.quiz.answers.period_${period}`)}</option>)}
          </XomicsSelect>}
        </div>
      </div>
    </div>
    {other && tab !== 'answers' && <Alert tone="info">{tx('ui.quiz.language_note', { language: languageLabel(languages.find((entry) => entry.code === language) || { name: language }) })}</Alert>}
    {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
    {undo && <Alert tone="info">{tx('ui.hub.section.removed')}{' '}
      <button type="button" className="xo-link" disabled={!write} onClick={putBack}><Undo2 aria-hidden="true" />{tx('ui.hub.section.undo')}</button></Alert>}

    {tab === 'questions' && <div className="xo-hub-cards">
      {quiz.questions.map((question, index) => <QuestionCard key={question.id} question={question} index={index} count={quiz.questions.length}
        open={openId === question.id} onToggle={() => setOpenId(openId === question.id ? null : question.id)} editor={editor} />)}
    </div>}
    {tab === 'recommends' && <RecommendsTab quiz={quiz} editor={editor} adding={adding} onAdding={setAdding} onSave={save} />}
    {tab === 'answers' && <AnswersTab quiz={quiz} languages={languages} days={days} />}

    {dirty.length > 0 && tab !== 'answers' && <div className="xo-hub-save" role="status">
      <span>{tx('ui.hub.save.dirty', { count: dirty.length })}</span>
      <button type="button" className="xo-btn-2" disabled={busy} onClick={() => setDrafts({})}>{tx('ui.hub.save.discard')}</button>
      <button type="button" className="xo-btn" disabled={!write} onClick={save}>{busy ? tx('ui.hub.save.saving') : tx('ui.hub.save.save')}</button>
    </div>}
  </div>;
}
